import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CubeEntry } from 'app/api/cube.api.service';
import { getError, LoadingState } from 'app/utils/callstate';
import * as _ from 'lodash';
import * as moment from 'moment';

import { selectFlatPlanAnalytics } from '../plan/plan.selectors';
import { CubeQuery } from './cube.actions';
import { CubeState } from './cube.reducer';
import * as fromCube from './cube.reducer';
import { AnalyticsSelectors } from 'app/main/analytics/selectors';
import { CompaniesSelectors } from 'app/main/company/selectors';

export const selectCubeState = createFeatureSelector<CubeState>('cube');

export const selectCube = createSelector(selectCubeState, fromCube.selectAll);

export const selectTimelineScale = createSelector(
  selectCubeState,
  (state) => state.timelineScale
);

export const selectCubeUntilDateExcluded = createSelector(
  selectCube,
  CompaniesSelectors.selectCurrentCompany,
  (cube, company) => {
    if (!company) {
      return cube;
    }
    const untilDateExcluded = moment.utc(company.untilDateExcluded);
    return _.isEmpty(company.untilDateExcluded)
      ? cube
      : cube.filter((entry: CubeEntry) =>
          moment.utc(entry.entryDate).isBefore(untilDateExcluded)
        );
  }
);

export const selectCubeAnalytics = createSelector(
  selectCubeUntilDateExcluded,
  selectFlatPlanAnalytics,
  AnalyticsSelectors.selectFlatCustomerRule,
  AnalyticsSelectors.selectFlatProviderRule,
  selectTimelineScale,
  (cube, plan, cust, prov, timelineScale) => {
    return cube.map((entry: CubeEntry) => {
      const rac1 = entry.account.slice(0, 1);
      const rac2 = entry.account.slice(0, 2);
      const rac3 = entry.account.slice(0, 3);
      const flux = (entry.credit || 0.0) - (entry.debit || 0.0);
      const gen2Label = `${rac2} - ${entry.gen2}`;
      const thirdParty = _.isEmpty(entry.auxiliary)
        ? gen2Label
        : _.isEmpty(entry.auxiliaryLabel)
        ? entry.auxiliary
        : entry.auxiliaryLabel;

      let thirdParty2 = '';

      const planEntry = plan.find((el) => el.account === entry.account);
      const foundPlan = !_.isEmpty(planEntry);
      let providerLabel = '';
      let providerCategory = 'Autres';
      if (rac1 === '6') {
        const re = prov.find(
          (p) =>
            (p.target === 'entryLabel' &&
              entry.entryLabel.toUpperCase().includes(p.name.toUpperCase())) ||
            (p.target === 'account' && entry.account.includes(p.name))
        );
        if (re) {
          providerLabel = re.providerLabel;
          providerCategory = re.providerCategory || 'Autres';
          thirdParty2 = providerLabel;
        }
      }
      let customerLabel = '';
      let customerCategory = 'Autres';
      if (rac1 === '7') {
        const re = cust.find(
          (c) =>
            (c.target === 'entryLabel' &&
              entry.entryLabel.toUpperCase().includes(c.name.toUpperCase())) ||
            (c.target === 'account' && entry.account.includes(c.name))
        );
        if (re) {
          customerLabel = re.customerLabel;
          customerCategory = re.customerCategory || 'Autres';
          thirdParty2 = customerLabel;
        }
      }

      const missingRule = !providerLabel && !customerLabel;

      const entryDate = moment.utc(entry.entryDate);
      let timeline =
        entry.journal === 'AN' ? '0 AN' : entryDate.format('YYYY-MM');
      if (timeline !== '0 AN') {
        switch (timelineScale) {
          case 'quarter':
            timeline = `${entryDate.format('YYYY')}-T${entryDate.format('Q')}`;
            break;
          case 'semester':
            const semester = Math.ceil(entryDate.quarter() / 2);
            timeline = `${entryDate.format('YYYY')}-S${semester}`;
            break;
          case 'year':
            timeline = entryDate.format('YYYY');
            break;
          default:
            // month
            break;
        }
      }

      return {
        ...entry,
        flux,
        movement: flux >= 0 ? 'Cash IN' : 'Cash OUT',
        gen2Label,
        thirdParty,
        rac1,
        rac2,
        rac3,
        entryDate: moment.utc(entry.entryDate).format('YYYY-MM'),
        credit: entry.credit || 0.0,
        debit: entry.debit || 0.0,
        missingRule,
        providerLabel:
          providerLabel || (rac1 === '6' ? `Autres ${planEntry.ana3Name}` : ''),
        providerCategory,
        customerLabel:
          customerLabel || (rac1 === '7' ? `Autres ${planEntry.ana3Name}` : ''),
        customerCategory,
        thirdParty2:
          thirdParty2 ||
          (rac1 === '6' || rac1 === '7' ? `Autres ${planEntry.ana3Name}` : ''),
        timeline,
        ana1Name: foundPlan ? planEntry.ana1Name : '',
        ana2Name: foundPlan ? planEntry.ana2Name : '',
        ana3Name: foundPlan ? planEntry.ana3Name : '',
        ana1SortOrder: foundPlan ? planEntry.ana1SortOrder : 0,
        ana2SortOrder: foundPlan ? planEntry.ana2SortOrder : 0,
        ana3SortOrder: foundPlan ? planEntry.ana3SortOrder : 0,
        paymentPeriod: foundPlan ? planEntry.paymentPeriod : '',
      };
    });
  }
);

export const selectCubeByRac1 = (rac1: string) =>
  createSelector(selectCubeAnalytics, (entries) =>
    _.filter(entries, (entry) => entry.rac1 === rac1)
  );
export const selectCubeForAccount1 = createSelector(
  selectCubeByRac1('1'),
  (cube) => cube
);
export const selectCubeForAccount2 = createSelector(
  selectCubeByRac1('2'),
  (cube) => cube
);
export const selectCubeForAccount3 = createSelector(
  selectCubeByRac1('3'),
  (cube) => cube
);
export const selectCubeForAccount4 = createSelector(
  selectCubeByRac1('4'),
  (cube) => cube
);
export const selectCubeForAccount5 = createSelector(
  selectCubeByRac1('5'),
  (cube) => cube
);
export const selectCubeForAccount6 = createSelector(
  selectCubeByRac1('6'),
  (cube) => cube
);
export const selectCubeForAccount7 = createSelector(
  selectCubeByRac1('7'),
  (cube) => cube
);

export const selectCubeForResultat = createSelector(
  selectCubeForAccount6,
  selectCubeForAccount7,
  (cubeAccount6, cubeAccount7) =>
    _.sortBy(_.concat(cubeAccount7, cubeAccount6), [
      'ana2SortOrder',
      'ana3SortOrder',
      'thirdParty2',
    ])
);

export const selectCubeForRevenue = createSelector(
  selectCubeForAccount7,
  //   (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder'])
  (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder', 'ana3Name', 'customerLabel'])
);
export const selectCubeForRevenueByCategory = createSelector(
  selectCubeForAccount7,
  (cube) => _.sortBy(cube, ['customerCategory', 'customerLabel'])
);
export const selectCubeForExpenses = createSelector(
  selectCubeForAccount6,
  //   (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder'])
  (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder', 'providerLabel'])
);
export const selectCubeForExpensesByCategory = createSelector(
  selectCubeForAccount6,
  (cube) => _.sortBy(cube, ['providerCategory', 'providerLabel'])
);
export const selectCubeForEquity = createSelector(
  selectCubeForAccount1,
  //   (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder'])
  (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder', 'customerLabel'])
);
export const selectCubeForInvest = createSelector(
  selectCubeForAccount2,
  //   (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder'])
  (cube) => _.sortBy(cube, ['ana2SortOrder', 'ana3SortOrder', 'customerLabel'])
);
export const selectCubeForThirdParty = createSelector(
  selectCubeForAccount4,
  //   (cube) => _.sortBy(cube, ['ana2SortOrder', 'thirdParty'])
  (cube) => _.sortBy(cube, ['ana2SortOrder', 'auxiliaryLabel'])
);
export const selectCubeForThirdParty2 = createSelector(
  selectCubeForAccount4,
  (cube) => _.sortBy(cube, ['ana2SortOrder', 'thirdParty', 'customerLabel'])
  //   (cube) => _.sortBy(cube, ['ana2SortOrder', 'thirdParty'])
);
export const selectCubeForBanks = createSelector(
  selectCubeAnalytics,
  (entries) =>
    _.sortBy(
      _.filter(
        entries,
        (entry) => entry.rac1 !== '5' && entry.journal === 'BQE'
      ),
      ['movement', 'gen2Label', 'entryLabel']
    )
);

export const selectCubeByAccount = (account: string) =>
  createSelector(selectCubeAnalytics, (entries) =>
    _.filter(entries, (entry) => entry.account === account)
  );

export const selectCubeByJournal = (journal: string) =>
  createSelector(selectCubeAnalytics, (entries) =>
    _.filter(entries, (entry) => entry.journal === journal)
  );

// To be used in master/details of Customers/Customer tab
export const selectCubeByCustomer = (key: string) =>
  createSelector(selectCubeAnalytics, (entries) =>
    _.filter(
      entries,
      (entry) => entry.rac1 === '7' && entry.customerLabel === key
    )
  );

// To be used in master/details of Providers/Provider tab
export const selectCubeByProvider = (key: string) =>
  createSelector(selectCubeAnalytics, (entries) =>
    _.filter(
      entries,
      (entry) => entry.rac1 === '6' && entry.providerLabel === key
    )
  );

export const selectCubeLoading = createSelector(
  selectCubeState,
  (cubeState) => cubeState.callState === LoadingState.Loading
);

export const selectCubeLoaded = createSelector(
  selectCubeState,
  (cubeState) => cubeState.callState === LoadingState.Loaded
);

export const selectCubeError = createSelector(selectCubeState, (cubeState) =>
  getError(cubeState.callState)
);

export const selectCubeQuery = createSelector(
  selectCubeState,
  (cubeState) =>
    ({
      companyId: cubeState.companyId,
      key: cubeState.key,
      filter: cubeState.filter,
    } as CubeQuery)
);
